import React from 'react';
import { Button as MUIButton, ButtonProps as MUIButtonProps, CircularProgress } from '@mui/material';
import { motion } from 'framer-motion';

interface ButtonProps extends Omit<MUIButtonProps, 'variant'> {
  variant?: 'primary' | 'secondary' | 'premium' | 'enterprise' | 'outlined' | 'text';
  loading?: boolean;
  fullWidth?: boolean;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  loading = false,
  children,
  className = '',
  fullWidth = false,
  disabled,
  ...props
}) => {
  const getMUIVariant = () => {
    switch (variant) {
      case 'primary':
      case 'secondary':
      case 'premium':
      case 'enterprise':
        return 'contained';
      case 'outlined':
        return 'outlined';
      case 'text':
        return 'text';
      default:
        return 'contained';
    }
  };

  const getVariantSx = () => {
    switch (variant) {
      case 'primary':
        return {
          bgcolor: 'primary.main',
          color: 'white',
          '&:hover': { bgcolor: 'primary.dark' },
        };
      case 'secondary':
        return {
          bgcolor: 'secondary.main',
          color: 'white',
          '&:hover': { bgcolor: 'secondary.dark' },
        };
      case 'premium':
        return {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
          },
        };
      case 'enterprise':
        return {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(135deg, #e878f2 0%, #ed3349 100%)',
          },
        };
      case 'outlined':
        return {
          borderColor: 'grey.300',
          color: 'grey.700',
          '&:hover': {
            bgcolor: 'grey.50',
            borderColor: 'grey.400',
          },
        };
      case 'text':
        return {
          color: 'primary.main',
          '&:hover': {
            bgcolor: 'rgba(108, 92, 231, 0.04)',
          },
        };
      default:
        return {};
    }
  };

  return (
    <motion.div
      whileHover={{ scale: disabled || loading ? 1 : 1.02 }}
      whileTap={{ scale: disabled || loading ? 1 : 0.98 }}
      style={{ width: fullWidth ? '100%' : 'auto', display: 'inline-block' }}
    >
      <MUIButton
        variant={getMUIVariant()}
        sx={{
          ...getVariantSx(),
          ...(className ? {} : {}), // Custom className support if needed
        }}
        disabled={disabled || loading}
        fullWidth={fullWidth}
        {...props}
      >
        {loading ? (
          <>
            <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />
            読み込み中...
          </>
        ) : (
          children
        )}
      </MUIButton>
    </motion.div>
  );
};

export default Button;